import { Box, Button, SvgIcon, Typography, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import styles from "./SearchRequestsTable.styles";
import SecureSyncTable from "src/components/common/SecureSyncTable/SecureSyncTable";
import SecureSyncTableCard from "src/components/common/SecureSyncTableCard/SecureSyncTableCard";
import RemoveRedEyeFilled from "src/components/Icons/RemoveRedEyeFilled/RemoveRedEyeFilled";
import SecureSyncIconTooltip from "src/components/common/SecureSyncIconTooltip/SecureSyncIconTooltip";
import { getRequestDetailPath, requestHome } from "src/constants/routeConstants/requestRouterFullPaths";
import { formatDate } from "src/utils/dateHelper";
import { getSearchRequestPayload } from "src/services/printServices";
import SecureSyncPrintModal from "src/components/common/SecureSyncPrintModal/SecureSyncPrintModal";
import { targetUrl } from "src/constants/printConstants";
import { useSecureSyncHotKeys } from "src/customHooks";
import { convertToLocaleUpperCase, getKeyBoardLabel } from "src/utils/commonHelper";
import { getShortCutKeys } from "src/constants/shortCutKeys";

const SearchRequestsTable = ({ records = [], isSearched, searchCriteria }) => {
    const shortCutKeys = getShortCutKeys();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();
    const location = useLocation();
    const hasRequests = records.length > 0;
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printPayload, setPrintPayload] = useState(null);
    useSecureSyncHotKeys({
        hotkeys: shortCutKeys.prepareReport,
        callback: () => {
            if (hasRequests) {
                printReport();
            }
        },
    });

    const columns = useMemo(
        () => [
            {
                field: "edit",
                headerName: "",
                width: 60,
                sortable: false,
                renderCell: (params) => (
                    <SecureSyncIconTooltip
                        title={t("searchRequests.viewRequestDetail")}
                        icon={
                            <SvgIcon
                                fill={theme.palette.primary.main}
                                component={RemoveRedEyeFilled}
                                onClick={() => handleEditClick(params)}
                            />
                        }
                    ></SecureSyncIconTooltip>
                ),
            },
            {
                field: "Request_Id",
                headerName: convertToLocaleUpperCase(t("tableHeader.requestId")),
                sortable: true,
            },
            {
                field: "Service_Date",
                headerName: convertToLocaleUpperCase(t("tableHeader.serviceData")),
                sortable: true,
                type: "date",
                renderCell: (params) => {
                    const formattedDate = formatDate(params.Service_Date);
                    return formattedDate;
                },
            },
            {
                field: "Request_Type",
                headerName: convertToLocaleUpperCase(t("tableHeader.requestType")),
                sortable: true,
            },
            {
                field: "Request_Status",
                headerName: convertToLocaleUpperCase(t("tableHeader.requestStatus")),
                sortable: true,
            },
            {
                field: "Request_Action",
                headerName: convertToLocaleUpperCase(t("tableHeader.requestAction")),
                sortable: true,
            },
            {
                field: "Personnel_Name",
                headerName: convertToLocaleUpperCase(t("tableHeader.requestBy")),
                sortable: true,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const handleEditClick = (params) => {
        // Navigate to the edit page and pass the row data
        navigate(getRequestDetailPath(params.Request_Id));
    };

    const onTableStateChange = (attributes) => {
        const locationState = location.state || {};
        navigate(requestHome, { state: { ...locationState, ...attributes }, replace: true });
    };

    const printReport = () => {
        const mediaList = [];
        records.forEach((e) => {
            mediaList.push(e.mediaNo);
        });
        setPrintPayload(
            getSearchRequestPayload({
                requestId: searchCriteria?.requestId,
                requestStatusId: searchCriteria?.requestStatusId,
                requestTypeId: searchCriteria?.requestTypeId,
                serviceDateFrom: searchCriteria?.serviceDateFrom,
                serviceDateTo: searchCriteria?.serviceDateTo,
            })
        );
        setIsPrintModalOpen(true);
    };

    return (
        <>
            <Box sx={styles.container}>
                <SecureSyncTableCard>
                    <Box sx={styles.recordCount}>
                        {hasRequests && (
                            <>
                                <Typography variant="body1">
                                    {t("messages.recordsFound", { count: records.length })}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        printReport();
                                    }}
                                    title={getKeyBoardLabel(shortCutKeys.prepareReport)}
                                >
                                    {t("buttonLabels.prepareReport")}
                                </Button>
                            </>
                        )}
                    </Box>
                    <SecureSyncTable
                        rows={records}
                        columns={columns}
                        initialPage={location.state?.pageNumber}
                        initialRowsPerPage={location.state?.rowsPerPage}
                        initialOrderBy={location.state?.orderBy}
                        initialOrder={location.state?.order}
                        noDataMessage={
                            isSearched ? t("messages.noMatchingRecords") : t("messages.pleaseProvideTheInput")
                        }
                        getRowId={(row) => row.Request_Id}
                        onTableAttributesChange={onTableStateChange}
                    />
                </SecureSyncTableCard>
                {hasRequests && (
                    <Typography sx={styles.footerCaption}>
                        <Trans i18nKey={"common.tableFooterCaption"}></Trans>
                    </Typography>
                )}
            </Box>
            <SecureSyncPrintModal
                open={isPrintModalOpen}
                payload={printPayload}
                onClose={() => setIsPrintModalOpen(false)}
                targetReport={targetUrl.searchRequestReport}
            />
        </>
    );
};

export default SearchRequestsTable;
